import { computed, Directive, HostListener, input, Optional, SkipSelf } from '@angular/core';

/**
 * services
 */
import { TableStateService } from '../../services';

/**
 * @directive TableSortableColumnDirective
 * @description Adds click-to-sort behavior to a table header cell.
 *
 * Host classes:
 * - sortable / cursor-pointer / user-select-none: applied when sorting is enabled
 * - sorted-asc / sorted-desc: applied based on current sort direction
 * - bg-dark / text-white: applied when column is currently sorted
 */
@Directive({
  selector: '[appTableSortableColumn]',
  host: {
    '[class.sortable]': '!skipSortable()',
    '[class.cursor-pointer]': '!skipSortable()',
    '[class.user-select-none]': '!skipSortable()',
    '[class.sorted-asc]': 'isSortedAsc()',
    '[class.sorted-desc]': 'isSortedDesc()',
    '[class.bg-dark]': 'isSortedAsc() || isSortedDesc()',
    '[class.text-white]': 'isSortedAsc() || isSortedDesc()',
  }
})
export class TableSortableColumnDirective {

  /**
   * Host listener for sort event
   */
  @HostListener('click', ['$event'])
  public onSort(event: MouseEvent): void {
    
    // skip if skipSortable is true
    if (this.skipSortable()) return;

    // get field
    const field = this.appTableSortableColumn();

    // check if field is valid
    if (!this.tableState) return;
    if (typeof field !== 'string' || field.trim().length === 0) return;

    // toggle sort
    const append = this.tableState.sortMode() === 'multiple' && (event.ctrlKey || event.metaKey);
    this.tableState.toggleSort(field, append);
  }

  /**
   * Input for table sortable column
   */
  public appTableSortableColumn = input<string>();
  
  /**
   * Input for skip sortable
   */
  public skipSortable = input<boolean>(false);

  /**
   * Current sort order for this column, or `null` when the column is unsorted
   * — or when the directive is used outside a table at all (see the
   * constructor). Every signal below reads the order through here so the
   * host bindings stay safe in both cases.
   */
  private readonly _sortOrder = computed(() => {
    return this.tableState?.getSortOrder(this.appTableSortableColumn() ?? '') ?? null;
  });

  /**
   * Computed signal for is sorted ascending
   */
  public readonly isSortedAsc = computed(() => this._sortOrder() === 'asc');

  /**
   * Computed signal for is sorted descending
   */
  public readonly isSortedDesc = computed(() => this._sortOrder() === 'desc');

  /**
   * Computed signal for is sort default
   */
  public readonly isSortDefault = computed(() => this._sortOrder() === null);

  /**
   * `@SkipSelf()` rather than `@Host()`, matching `TableSearchComponent`,
   * `TableCheckboxComponent` and `TableHeaderCheckboxComponent`.
   *
   * `TableStateService` is provided on `TableComponent`, so it lives on the
   * `<app-table>` host element's injector. `@Host()` reaches that only from a
   * *projected* `<th>` — i.e. one written in a consumer's `theadTemplate`.
   * From the table's own default `thead`, whose `<th>` sits inside
   * `TableComponent`'s view, the search stops before the host element and
   * `@Optional()` turns the miss into `null`.
   */
  constructor(
    @Optional()
    @SkipSelf()
    public readonly tableState: TableStateService
  ) {}

}
